import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField as MuiTextField,
  Typography,
  Box
} from '@mui/material';
import { useRecordContext, useDataProvider, useNotify, useRefresh } from 'react-admin';
import { PaymentDetail, PaymentStatusLabels } from './PaymentTypes';

interface PaymentStatusButtonProps {
  canEdit?: boolean;
  apiBasePath?: string;
}

const statusOptions = [
  { value: 2, label: '未振込' },
  { value: 3, label: '振込済み' },
  { value: 4, label: '失効' },
  { value: 99, label: 'エラー' }
];

export const PaymentStatusButton: React.FC<PaymentStatusButtonProps> = ({
  canEdit = true,
  apiBasePath = 'payments'
}) => {
  const record = useRecordContext<PaymentDetail>();
  const [open, setOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<number>(2);
  const [memo, setMemo] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record || record.paymentStatus === 1) {
    // 保留中の場合はボタンを表示しない
    return null;
  }

  const handleOpen = () => {
    setNewStatus(record.paymentStatus);
    setMemo(record.memo || '');
    setOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!canEdit) return;

    setLoading(true);
    try {
      const updateData: { paymentStatus: number; memo: string } = {
        paymentStatus: newStatus,
        memo: memo.trim()
      };

      await dataProvider.update(`${apiBasePath}/details`, {
        id: record.id,
        data: updateData,
        previousData: record
      });
      notify('ステータスを更新しました', { type: 'success' });
      setOpen(false);
      refresh();
    } catch (error) {
      notify('ステータスの更新に失敗しました', { type: 'error' });
      console.error('Failed to update payment status:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="outlined" onClick={handleOpen} disabled={loading} size="small" sx={{ whiteSpace: 'nowrap' }}>
        {canEdit ? 'ステータス変更' : 'メモ'}
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{canEdit ? 'ステータス変更' : 'メモ'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {canEdit ? (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>新しいステータス</InputLabel>
              <Select value={newStatus} label="新しいステータス" onChange={(e) => setNewStatus(e.target.value as number)}>
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                現在のステータス
              </Typography>
              <Typography variant="body1">
                {PaymentStatusLabels[record.paymentStatus] || '不明'}
              </Typography>
            </Box>
          )}

          {canEdit ? (
            <MuiTextField
              fullWidth
              label="メモ（任意）"
              multiline
              rows={4}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder={'メモを入力してください'}
              InputProps={{
                sx: {
                  p: 2
                }
              }}
            />
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary">
                メモ
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {record.memo || '（メモなし）'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            {canEdit ? 'キャンセル' : '閉じる'}
          </Button>
          {canEdit && (
            <Button onClick={handleUpdateStatus} variant="contained" disabled={loading}>
              {loading ? '更新中...' : '更新'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentStatusButton;
